import { useState } from "react";
import {
  Steps, Form, Input, Button, message, Spin, Tag, Row, Col, Divider,
} from "antd";
import { Building2, Package, Users, Factory, CheckCircle, Info } from "lucide-react";
import { usePlanList } from "../../../api-hooks/plans";
import { useIndustryList } from "../../../api-hooks/industries";
import { useCompanySave } from "../../../api-hooks/companies";
import { TPlan, TIndustry, TCreateCompanyInput } from "../../../types";

const TERM_KEYS = ["job", "worker", "client", "location", "quote", "invoice"];

const UK_PHONE_RE = /^(\+44\s?|0)[\d\s\-()]{9,14}$/;
const UK_POST_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

const isFeatureEnabled = (val: any) => val === "1" || val === "true";

const LUCIDE_FALLBACK: Record<string, string> = {
  bug: "🐛", flame: "🔥", wind: "❄️", zap: "⚡", droplets: "🚿",
  sparkles: "🧹", shield: "🛡️", "tree-pine": "🌿", wrench: "🔧",
  "hard-hat": "🏗️", map: "🌍",
};

const resolveIcon = (icon: string | undefined) =>
  !icon ? "🏭" : LUCIDE_FALLBACK[icon] ?? icon;

const WIZARD_STEPS = [
  { title: "Plan", icon: <Package size={14} /> },
  { title: "Company", icon: <Building2 size={14} /> },
  { title: "Admin User", icon: <Users size={14} /> },
  { title: "Industries", icon: <Factory size={14} /> },
];

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function CreateCompanyWizard({ onSuccess, onCancel }: Props) {
  const [step, setStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<TPlan | null>(null);
  const [selectedIndustries, setSelectedIndustries] = useState<number[]>([]);
  const [hoveredIndustry, setHoveredIndustry] = useState<TIndustry | null>(null);
  const [formData, setFormData] = useState<Partial<TCreateCompanyInput>>({});
  const [messageApi, ctx] = message.useMessage();
  const [companyForm] = Form.useForm();
  const [adminForm] = Form.useForm();

  const { data: plans = [], isLoading: plansLoading } = usePlanList({ limit: 100, features: "1" });
  const { data: industries = [], isLoading: industriesLoading } = useIndustryList({ limit: 200, terminology: "1" });

  const activePlans = plans.filter((p) => p.is_active);
  const activeIndustries = industries.filter((i) => i.is_active);

  const { mutate: createCompany, isPending: submitting } = useCompanySave({
    onSuccess: () => {
      messageApi.success("Company created successfully!");
      onSuccess();
    },
    onError: (err: any) => {
      messageApi.error(err?.message || "Failed to create company.");
    },
  });

  const toggleIndustry = (id: number) => {
    setSelectedIndustries([id]);
  };

  const handleCompanyNext = () => {
    companyForm.validateFields().then((vals) => {
      setFormData((prev) => ({ ...prev, ...vals }));
      setStep(2);
    });
  };

  const handleAdminNext = () => {
    adminForm.validateFields().then((vals) => {
      const { admin_confirm_password, ...rest } = vals;
      setFormData((prev) => ({ ...prev, ...rest }));
      setStep(3);
    });
  };

  const handleSubmit = () => {
    if (selectedIndustries.length === 0) {
      messageApi.warning("Select at least one industry.");
      return;
    }
    const payload: TCreateCompanyInput = {
      ...(formData as any),
      plan_id: selectedPlan!.id,
      industry_ids: selectedIndustries,
      primary_industry_id: selectedIndustries[0],
    };
    createCompany(payload);
  };

  if (plansLoading || industriesLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Spin size="large" />
        <p className="text-slate-400 text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {ctx}
      <Steps
        current={step}
        size="small"
        className="mb-8"
        items={WIZARD_STEPS}
      />

      {/* Step 0: Plan selection */}
      {step === 0 && (
        <div>
          <p className="text-slate-500 text-sm mb-5">Choose the subscription plan for this company.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {activePlans.map((plan) => {
              const selected = selectedPlan?.id === plan.id;
              const enabledFeatures = plan.features
                ? Object.entries(plan.features).filter(([, v]) => isFeatureEnabled(v)).map(([k]) => k.replace(/_/g, " "))
                : [];

              return (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan)}
                  className={`border-2 rounded-2xl p-5 cursor-pointer transition-all relative ${
                    selected
                      ? "border-violet-600 bg-violet-50 shadow-md"
                      : "border-slate-200 bg-white hover:border-violet-300 hover:shadow-sm"
                  }`}
                >
                  {selected && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle size={18} className="text-violet-600" />
                    </div>
                  )}
                  <div className="font-bold text-slate-800 text-base mb-1">{plan.name}</div>
                  <div className="text-xs text-slate-500 mb-3 line-clamp-2">{plan.description}</div>

                  <div className="text-2xl font-bold text-violet-700 mb-1">
                    £{Number(plan.price_monthly).toFixed(0)}
                    <span className="text-sm font-normal text-slate-400">/mo</span>
                  </div>

                  <div className="flex flex-wrap gap-1 mt-3">
                    <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {plan.max_users != null ? `${plan.max_users} users` : "∞ users"}
                    </span>
                    <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {plan.max_jobs_per_month != null ? `${plan.max_jobs_per_month} jobs/mo` : "∞ jobs"}
                    </span>
                    <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      {plan.max_storage_gb != null ? `${plan.max_storage_gb}GB` : "∞ storage"}
                    </span>
                  </div>

                  {enabledFeatures.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="text-[10px] text-slate-400 mb-1.5">Includes:</div>
                      <div className="flex flex-wrap gap-1">
                        {enabledFeatures.slice(0, 6).map((f) => (
                          <span key={f} className="text-[10px] bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded capitalize">
                            {f}
                          </span>
                        ))}
                        {enabledFeatures.length > 6 && (
                          <span className="text-[10px] text-slate-400">+{enabledFeatures.length - 6} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {activePlans.length === 0 && (
              <div className="col-span-3 text-center py-12 text-slate-400">
                No active plans available. Create a plan first.
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedPlan}
              onClick={() => setStep(1)}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              Next: Company Details
            </Button>
          </div>
        </div>
      )}

      {/* Step 1: Company details */}
      {step === 1 && (
        <div>
          <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-xl px-4 py-2 mb-5">
            <span className="text-xs text-violet-700">
              Plan: <strong>{selectedPlan?.name}</strong> · £{Number(selectedPlan?.price_monthly).toFixed(0)}/mo
            </span>
            <button onClick={() => setStep(0)} className="ml-auto text-[10px] text-violet-500 underline">Change</button>
          </div>

          <Form form={companyForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Company Name"
                  rules={[{ required: true, message: "Company name is required" }, { min: 2, message: "Min 2 characters" }]}
                >
                  <Input placeholder="Acme Services Ltd" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Company Email"
                  rules={[{ required: true, message: "Email is required" }, { type: "email", message: "Enter a valid email" }]}
                >
                  <Input placeholder="info@company.co.uk" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Phone"
                  rules={[
                    {
                      pattern: UK_PHONE_RE,
                      message: "Enter a valid UK phone number (e.g. +44 20 1234 5678 or 07700 900000)",
                    },
                  ]}
                >
                  <Input placeholder="+44 20 1234 5678" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="website"
                  label="Website"
                  rules={[{ type: "url", message: "Enter a valid URL (include https://)" }]}
                >
                  <Input placeholder="https://company.co.uk" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="company_registration_number" label="Company Reg. No." extra="8-digit Companies House number">
                  <Input placeholder="12345678" className="rounded-lg" maxLength={8} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="vat_number" label="VAT Number" extra="Format: GB followed by 9 digits">
                  <Input placeholder="GB123456789" className="rounded-lg" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Divider orientation="left" className="text-xs text-slate-400">Address</Divider>
              </Col>

              <Col span={24}>
                <Form.Item name="address_line1" label="Address Line 1">
                  <Input placeholder="123 High Street" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="city" label="City">
                  <Input placeholder="London" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="postcode"
                  label="Postcode"
                  rules={[{ pattern: UK_POST_RE, message: "Enter a valid UK postcode (e.g. EC1A 1BB)" }]}
                >
                  <Input placeholder="EC1A 1BB" className="rounded-lg" style={{ textTransform: "uppercase" }} />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <div className="flex gap-3">
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button
              type="primary"
              onClick={handleCompanyNext}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              Next: Admin User
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Admin user */}
      {step === 2 && (
        <div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 text-xs text-amber-700 leading-relaxed">
            This user will be the <strong>Super Admin</strong> for <strong>{formData.name as string}</strong> with
            full access to all company features. They can add more users once logged in.
          </div>

          <Form form={adminForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="admin_first_name"
                  label="First Name"
                  rules={[{ required: true, message: "First name is required" }]}
                >
                  <Input placeholder="John" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="admin_last_name"
                  label="Last Name"
                  rules={[{ required: true, message: "Last name is required" }]}
                >
                  <Input placeholder="Smith" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="admin_email"
                  label="Admin Email"
                  rules={[{ required: true, message: "Email is required" }, { type: "email", message: "Enter a valid email" }]}
                >
                  <Input placeholder="admin@company.co.uk" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="admin_phone"
                  label="Admin Phone"
                  rules={[{ pattern: UK_PHONE_RE, message: "Enter a valid UK phone number" }]}
                >
                  <Input placeholder="+44 7700 900000" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="admin_password"
                  label="Temporary Password"
                  rules={[{ required: true, message: "Password is required" }, { min: 8, message: "Min 8 characters" }]}
                >
                  <Input.Password placeholder="Min 8 characters" className="rounded-lg" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="admin_confirm_password"
                  label="Confirm Password"
                  dependencies={["admin_password"]}
                  rules={[
                    { required: true, message: "Please confirm the password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("admin_password") === value) return Promise.resolve();
                        return Promise.reject(new Error("Passwords do not match"));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="Re-enter password" className="rounded-lg" />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <div className="flex gap-3">
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button
              type="primary"
              onClick={handleAdminNext}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              Next: Industries
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Industries */}
      {step === 3 && (
        <div>
          <p className="text-slate-500 text-sm mb-4">
            Select the primary industry for this company. This drives the terminology throughout the app.
            Hover to preview industry terminology.
          </p>

          <div className="flex gap-4">
            <div className="flex-1">
              <div className="grid grid-cols-2 gap-2">
                {activeIndustries.map((ind) => {
                  const selected = selectedIndustries.includes(ind.id);
                  const isPrimary = selectedIndustries[0] === ind.id;
                  return (
                    <div
                      key={ind.id}
                      onClick={() => toggleIndustry(ind.id)}
                      onMouseEnter={() => setHoveredIndustry(ind)}
                      onMouseLeave={() => setHoveredIndustry(null)}
                      className={`border-2 rounded-xl p-3 cursor-pointer transition-all flex items-center gap-2.5 ${
                        selected
                          ? "border-violet-600 bg-violet-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-violet-300"
                      }`}
                    >
                      <span className="text-xl">{resolveIcon(ind.icon)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{ind.name}</div>
                        {isPrimary && selected && (
                          <Tag color="purple" className="text-[9px] mt-0.5 leading-none">Primary</Tag>
                        )}
                      </div>
                      {selected && <CheckCircle size={14} className="text-violet-600 shrink-0" />}
                    </div>
                  );
                })}

                {activeIndustries.length === 0 && (
                  <div className="col-span-2 text-center py-8 text-slate-400 text-sm">
                    No active industries. Create industries first.
                  </div>
                )}
              </div>

              {selectedIndustries.length > 0 && (
                <div className="mt-3 px-1 text-xs text-slate-500">
                  <strong>{activeIndustries.find(i => i.id === selectedIndustries[0])?.name}</strong> selected as primary industry
                </div>
              )}
            </div>

            <div className="w-52 shrink-0">
              {hoveredIndustry ? (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 sticky top-0">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">{resolveIcon(hoveredIndustry.icon)}</span>
                    <div>
                      <div className="text-xs font-semibold text-violet-800">{hoveredIndustry.name}</div>
                      <div className="text-[10px] text-violet-500">Custom terminology</div>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {TERM_KEYS.map((key) => {
                      const term = hoveredIndustry.terminology?.find((t) => t.term_key === key);
                      return (
                        <div key={key} className="flex justify-between text-[11px]">
                          <span className="text-slate-500 capitalize">{key}</span>
                          <span className="text-slate-800 font-medium">
                            {term?.term_singular ? `${term.term_singular} / ${term.term_plural}` : "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                  <Info size={16} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-[11px] text-slate-400">Hover an industry to preview its custom terminology</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 mt-5 pt-4 border-t border-slate-100">
            <Button onClick={() => setStep(2)}>Back</Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={handleSubmit}
              disabled={selectedIndustries.length === 0}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              Create Company
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
